import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Observable, ReplaySubject, tap } from 'rxjs';
import { AdminNavigationService } from './admin-navigation.service';
import { AuthRepository, UserRole } from '../auth/auth.repository';
import { FeatureSettingsService } from 'app/services/feature-settings.service';
import { environment } from '@env/environment';

@Injectable({providedIn: 'root'})
export class NavigationService
{
    private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _adminNavigationService: AdminNavigationService,
        private _authRepository: AuthRepository,
        private _featureSettings: FeatureSettingsService
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation>
    {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation>
    {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            tap((navigation) =>
            {
                const user = this._authRepository.getUser();
                const role: UserRole | undefined = user?.role;

                const isAdmin = this._adminNavigationService.hasAdminRole();
                const allowedForSupervisorAndCourses = role === UserRole.SuperUser || role === UserRole.SysAdmin || role === UserRole.CourseAdmin || role === UserRole.Supervisor;

                // Feature flags — use FeatureSettingsService for dynamic company-level overrides
                const featureClassroom = this._featureSettings.isFeatureEnabled('feature_classroom');
                const featureSupervisor = !!environment.feature_supervisor;
                const featureStudyMatrix = !!environment.feature_study_matrix;
                const featureStudentGroups = !!environment.feature_student_groups;
                const featureReports = !!environment.feature_reports;

                // Update admin navigation visibility based on user role
                if (isAdmin) {
                    if (!navigation.admin) {
                        navigation.admin = [];
                    }
                } else {
                    navigation.admin = [];
                }

                // Settings visibility: SuperUser, SysAdmin, and CourseAdmin (limited)
                const allowedForSettings = role === UserRole.SuperUser || role === UserRole.SysAdmin || role === UserRole.CourseAdmin;

                // Helper to filter a nav array by id rules (recursive — handles nested children)
                const filterNav = (items?: any[]): any[] => {
                    if (!Array.isArray(items)) return items ?? [];
                    return items.filter((item) => {
                        if (!item || !item.id) return true;

                        // Students/Contacts: Visible to SuperUser, SysAdmin, CourseAdmin, Supervisor
                        if (item.id === 'students') {
                            return role !== UserRole.Student && allowedForSupervisorAndCourses;
                        }

                        // Supervisors management: only SuperUser, SysAdmin, CourseAdmin
                        if (item.id === 'supervisors') {
                            return role === UserRole.SuperUser || role === UserRole.SysAdmin || role === UserRole.CourseAdmin;
                        }

                        // Supervisor and Courses visibility: only admin/supervisor
                        if (item.id === 'supervisor' || item.id === 'courses') {
                            return allowedForSupervisorAndCourses && featureSupervisor;
                        }

                        // Course Admin menu: only CourseAdmin + SuperUser + SysAdmin
                        if (item.id === 'course-admin') {
                            return role === UserRole.SuperUser || role === UserRole.SysAdmin || role === UserRole.CourseAdmin;
                        }

                        // Study Matrix: SuperUser only (+ feature flag)
                        if (item.id === 'course-admin.study-matrix') {
                            return featureStudyMatrix && role === UserRole.SuperUser;
                        }

                        // Student Groups: SuperUser only (+ feature flag)
                        if (item.id === 'course-admin.groups') {
                            return featureStudentGroups && role === UserRole.SuperUser;
                        }

                        // Supervisor classroom dashboard: supervisor/courseadmin/superuser + feature flag
                        if (item.id === 'supervisor-classroom') {
                            return featureClassroom && allowedForSupervisorAndCourses;
                        }

                        // Classroom admin settings page requires feature flag
                        if (item.id === 'vclass-admin') {
                            return featureClassroom;
                        }

                        // Reports: hidden unless feature flag is enabled
                        if (item.id === 'reports') {
                            return featureReports;
                        }

                        // Settings: SuperUser, SysAdmin, and CourseAdmin (limited children)
                        if (item.id === 'settings') {
                            if (allowedForSettings && item.children) {
                                if (role === UserRole.CourseAdmin) {
                                    item.children = item.children.filter((c: any) => c.id === 'settings.system-settings');
                                } else if (role !== UserRole.SuperUser && role !== UserRole.SysAdmin) {
                                    item.children = item.children.filter((c: any) => c.id !== 'settings.system');
                                }
                            }
                            return allowedForSettings;
                        }

                        return true;
                    }).map((item) => {
                        // Recurse into children so nested items are also filtered
                        if (item.children && item.children.length > 0) {
                            item.children = filterNav(item.children);
                        }
                        return item;
                    });
                };

                // Apply filters to both admin and compact navigations
                navigation.admin = filterNav(navigation.admin);
                navigation.compact = filterNav(navigation.compact);

                // Inject runtime function handlers (cloneDeep strips functions)
                this._injectNavFunctions(navigation);

                this._navigation.next(navigation);
            }),
        );
    }

    // ─────────────────────────────────────────────────────────────────────
    // @ Private helpers
    // ─────────────────────────────────────────────────────────────────────

    /**
     * Walk all nav arrays and attach runtime `function` handlers to items
     * whose handlers cannot survive a cloneDeep pass.
     */
    private _injectNavFunctions(navigation: Navigation): void {
        const walk = (items?: FuseNavigationItem[]) => {
            if (!items) { return; }
            for (const item of items) {
                if (item.id === 'course-admin.legacy-creation') {
                    item.function = () => this._openLegacyCourseCreation();
                }
                if (item.children) { walk(item.children); }
            }
        };
        walk(navigation.admin);
        walk(navigation.compact);
    }

    /**
     * Open the legacy course-creation editor in a new browser tab
     * using the same belogin.php SSO flow as the courses detail page.
     */
    private _openLegacyCourseCreation(): void {
        const user = this._authRepository.getUser();
        if (!user) { return; }

        const accessToken = this._authRepository.getAccessToken();
        const redirectTarget = 'otplus/dashboard-course.php';

        const form = document.createElement('form');
        form.method = 'POST';
        form.action = environment.otrainingsystem + '/belogin.php';
        form.target = '_blank';
        form.style.display = 'none';

        const fields: { name: string; value: string }[] = [
            { name: 'Authorization', value: accessToken || '' },
            { name: 'uid',           value: String(user.user_id) },
            { name: 'fid',           value: String(user.person_id) },
            { name: 'role',          value: String(user.role) },
            { name: 'redirect',      value: redirectTarget },
        ];

        fields.forEach(({ name, value }) => {
            const input = document.createElement('input');
            input.type  = 'hidden';
            input.name  = name;
            input.value = value;
            form.appendChild(input);
        });

        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    }
}
